import { useState } from 'react';
import { Loader2, User, Mail, Shield } from 'lucide-react';
import { platformFetch } from '../../platform/http';
import type { UserData } from './types';

interface UserEditFormProps {
  user: UserData;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UserEditForm({ user, onClose, onSuccess }: UserEditFormProps) {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email,
    role: user.role,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await platformFetch(`/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        onSuccess();
      } else {
        setError(data.error || '更新失败');
      }
    } catch (err) {
      setError('更新失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">
          昵称
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="用户昵称"
            className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">
          邮箱 <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            required
            className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">
          角色 <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full bg-white border border-light-border rounded-xl pl-10 pr-4 py-3 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors appearance-none cursor-pointer"
          >
            <option value="user">普通用户</option>
            <option value="admin">管理员</option>
          </select>
        </div>
        <p className="text-xs text-light-muted mt-1.5">
          管理员拥有后台管理权限，请谨慎设置
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2.5 border border-light-border text-light-text rounded-xl hover:bg-light-hover transition-colors"
        >
          取消
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2.5 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              保存中...
            </>
          ) : (
            '保存修改'
          )}
        </button>
      </div>
    </form>
  );
}
